import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from '../contexts/SubscriptionContext';
import "./DocumentView.css";
import '../components/primitives/Button.css';
import { DocumentApi, DocumentMetadataApi } from '../services/ApiService'; // removed EndeavorApi and FileApi
import { DocumentEndeavorApi } from '../services/api/DocumentEndeavorAPI';
import AIChatCard from "../components/aiChat/AiChatCard";
import DocumentPreview from "../components/DocumentPreview";
import Tabs from "../components/primitives/Tabs/Tabs";
import DocumentNotes from "../components/notes/DocumentNotes";
import StudyToolsTab from "../components/documents/StudyToolsTab";
import { useAiChat } from '../hooks/useAiChat';

// The order you want for your fields
const fieldOrder = [
  "Endeavor",
  "Title",
  "Author",
  "Date",
  "Important Names & Events",
  "Important Names and Events",
  "Key Topics",
  "Summary",
  "Document Text",
  "Document Text Translated from English to Spanish",
  "Notes",
];

// Sort metadata by fieldOrder
function sortMetadata(metaArray) {
  const getIndex = (fieldName) => {
    const i = fieldOrder.findIndex(
      (f) => f.toLowerCase() === fieldName.toLowerCase()
    );
    return i === -1 ? 9999 : i;
  };

  return [...metaArray].sort((a, b) => {
    const nameA = a.metadata_definition?.name || a.metadataName || "";
    const nameB = b.metadata_definition?.name || b.metadataName || "";
    const iA = getIndex(nameA);
    const iB = getIndex(nameB);
    if (iA !== iB) return iA - iB;
    return nameA.localeCompare(nameB);
  });
}

// Custom splitting to avoid splitting "March 5, 1770"
function customSplitOnCommas(str) {
  return str
    .split(/,(?!\s*\d)/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Parse as JSON array or fallback to custom comma split
function parseAsArray(str) {
  if (!str || typeof str !== "string") return [];
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (err) {
    // ignore error, fallback below
  }
  return customSplitOnCommas(str);
}

// Enhanced field value rendering
function renderFieldValue(fieldName, fieldValue, isEditing = false, onEditChange = null) {
  if (isEditing && onEditChange) {
    // Render editable field
    const lower = fieldName.toLowerCase();
    
    if (
      lower === "key topics" ||
      lower === "important names & events" ||
      lower === "important names and events"
    ) {
      // For tag fields, render as textarea with comma-separated values
      const currentValue = Array.isArray(fieldValue) ? fieldValue.join(', ') : fieldValue || '';
      return (
        <textarea
          className="metadata-edit-textarea"
          value={currentValue}
          onChange={(e) => onEditChange(fieldName, e.target.value)}
          placeholder="Enter comma-separated values"
          rows={2}
        />
      );
    } else if (typeof fieldValue === 'string' && fieldValue.length > 100) {
      // For long text, use textarea
      return (
        <textarea
          className="metadata-edit-textarea"
          value={fieldValue || ''}
          onChange={(e) => onEditChange(fieldName, e.target.value)}
          placeholder="Enter text"
          rows={4}
        />
      );
    } else {
      // For short text, use input
      return (
        <input
          type="text"
          className="metadata-edit-input"
          value={fieldValue || ''}
          onChange={(e) => onEditChange(fieldName, e.target.value)}
          placeholder="Enter value"
        />
      );
    }
  }

  // Regular display mode (not editing)
  if (!fieldValue) return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No value</span>;

  const lower = fieldName.toLowerCase();

  // If Key Topics or Important Names and Events, show as pill tags
  if (
    lower === "key topics" ||
    lower === "important names & events" ||
    lower === "important names and events"
  ) {
    const items = parseAsArray(fieldValue);
    return (
      <div className="tag-container">
        {items.map((tag, idx) => (
          <span key={idx} className="tag-pill">
            {tag}
          </span>
        ))}
      </div>
    );
  }

  // For long text fields, add proper formatting
  if (typeof fieldValue === 'string' && fieldValue.length > 200) {
    return (
      <div className="long-text-container">
        {fieldValue}
      </div>
    );
  }

  // Otherwise, just return formatted text
  return (
    <div style={{ 
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
      lineHeight: '1.6'
    }}>
      {fieldValue}
    </div>
  );
}

function DocumentView({ documentIdOverride = null, embedded = false, workspaceMode, onSwitchToBrowse, onSwitchToFocus, focusTabIndex = 0 } = {}) {
  const { id: idParam } = useParams();
  const id = documentIdOverride ?? idParam;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { loadExistingConversations, loadConversationMessages } = useAiChat('all');

  // When embedded in DocumentsPage, workspaceMode controls sidebar visibility.
  // In standalone mode (no workspaceMode), always show the sidebar.
  const showRightSidebar = !workspaceMode || workspaceMode === 'focus';

  const [doc, setDoc] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Chat state
  const [documentConversation, setDocumentConversation] = useState(null);
  const chatPanelWrapperRef = useRef(null); // wrapper whose height we set to available viewport space
  const conversationLoadedRef = useRef(false); // track if we've loaded a conversation to prevent race conditions

  // ✅ ADD: Associated courses state
  const [associatedCourses, setAssociatedCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);

  // Reset conversation state when document changes
  useEffect(() => {
    console.log('[DocumentView] Document changed, resetting conversation state', { docId: doc?.id });
    setDocumentConversation(null);
    conversationLoadedRef.current = false;
  }, [doc?.id]);

  // Search for existing conversation for this document
  useEffect(() => {
    if (!doc?.name || (!user?.id && !user?.userId)) {
      console.log('[DocumentView] Skipping conversation search - missing doc name or user', { 
        docName: doc?.name, 
        userId: user?.id || user?.userId 
      });
      return;
    }
    
    // Skip if conversation was already loaded for this specific document (e.g., via onConversationActivated)
    // Note: conversationLoadedRef is reset when document changes, so this only prevents duplicate loads for the same document
    if (conversationLoadedRef.current) {
      console.log('[DocumentView] Skipping conversation search - already loaded via callback');
      return;
    }
    
    const targetTitle = `Document: ${doc.name}`;
    const userId = user.id || user.userId;
    
    console.log('[DocumentView] Searching for conversation with title:', targetTitle);
    
    loadExistingConversations(userId)
      .then(conversations => {
        console.log('[DocumentView] Loaded conversations:', conversations.length, 'total');
        
        // Find the most recent conversation with matching title
        const matchingConversations = conversations.filter(c => 
          c.title && c.title.trim() === targetTitle.trim()
        );
        
        console.log('[DocumentView] Matching conversations found:', matchingConversations.length, matchingConversations);
        
        if (matchingConversations.length > 0) {
          // Sort by most recent (assuming higher ID or updatedAt)
          const mostRecent = matchingConversations.sort((a, b) => {
            // If there's an updatedAt field, use it
            if (a.updatedAt && b.updatedAt) {
              return new Date(b.updatedAt) - new Date(a.updatedAt);
            }
            // Otherwise use ID (higher ID = more recent)
            return (b.conversationId || b.id) - (a.conversationId || a.id);
          })[0];
          
          console.log('[DocumentView] Setting most recent conversation:', mostRecent);
          
          // Load messages for this conversation
          const convId = mostRecent.id || mostRecent.conversationId;
          loadConversationMessages(convId)
            .then(result => {
              console.log('[DocumentView] Loaded messages for conversation:', result);
              // Attach messages to conversation object
              const conversationWithMessages = {
                ...mostRecent,
                conversationId: convId,
                messages: result.messages || [],
                messagePagination: result.pagination
              };
              setDocumentConversation(conversationWithMessages);
              conversationLoadedRef.current = true;
            })
            .catch(err => {
              console.error('[DocumentView] Failed to load messages:', err);
              // Still set conversation even if messages fail to load
              setDocumentConversation(mostRecent);
              conversationLoadedRef.current = true;
            });
        } else {
          console.log('[DocumentView] No matching conversation found');
          setDocumentConversation(null);
        }
      })
      .catch(err => {
        console.error('DocumentView: Failed to load conversations:', err);
        setDocumentConversation(null);
      });
  }, [doc?.name, user?.id, user?.userId, loadExistingConversations, loadConversationMessages]);

  // ✅ ADD: Load associated courses
  const loadAssociatedCourses = useCallback(async () => {
    const uid = user?.id || user?.userId;
    if (!doc?.id || !uid) {
      console.log('DocumentView: Skipping course load - no document or user ID');
      return;
    }

    try {
      setCoursesLoading(true);
      const userId = uid;
      
      console.log('DocumentView: Loading associated courses for document:', doc.id, 'user:', userId);
      
      const courses = await DocumentEndeavorApi.getExistingEndeavorsForDocument(doc.id, userId);
      console.log('DocumentView: Loaded associated courses:', courses);
      
      setAssociatedCourses(courses || []);
      
    } catch (error) {
      console.error('DocumentView: Error loading associated courses:', error);
      setAssociatedCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  }, [doc?.id, user?.id, user?.userId]);

  // Fetch the document & metadata
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        
        const response = await DocumentApi.getDocument(id);
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }
        
        const docData = await response.json();
        if (!docData || !docData.id) {
          console.error("Document not found:", docData);
          setDoc(null);
          return;
        }
        
        console.log('DocumentView: Fetched document data:', docData);
        setDoc(docData);
        
        // Fetch metadata separately
        try {
          const metadataResponse = await DocumentMetadataApi.getDocumentMetadata(docData.id);
          let metadataArray = [];
          
          if (metadataResponse.ok) {
            const metadataData = await metadataResponse.json();
            console.log('DocumentView: Fetched metadata:', metadataData);
            const rawMetadata = Array.isArray(metadataData) ? metadataData : (metadataData.metadata || []);
            
            // Remove duplicates and empty values
            const uniqueMetadata = [];
            const seenFields = new Set();
            
            rawMetadata.forEach(meta => {
              const fieldName = meta.metadata_definition?.name || meta.metadataName || `Field #${meta.id}`;
              const normalizedFieldName = fieldName.toLowerCase().trim();
              
              if (seenFields.has(normalizedFieldName)) {
                console.log(`DocumentView: Skipping duplicate field: ${fieldName}`);
                return;
              }
              
              if (!meta.value || (typeof meta.value === 'string' && meta.value.trim() === '')) {
                console.log(`DocumentView: Skipping empty field: ${fieldName}`);
                return;
              }
              
              seenFields.add(normalizedFieldName);
              uniqueMetadata.push(meta);
            });
            
            metadataArray = uniqueMetadata;
          } else {
            console.warn("Failed to fetch metadata:", metadataResponse.status);
            metadataArray = docData.metadata || [];
          }
          
          // Add endeavor as synthetic metadata if document has one
          if (docData.endeavor) {
            const endeavorMetadata = {
              id: 'endeavor',
              metadataName: 'Endeavor',
              value: docData.endeavor.name,
              endeavorId: docData.endeavor.id,
              isEndeavor: true,
              metadata_definition: { name: 'Endeavor' }
            };
            metadataArray = [endeavorMetadata, ...metadataArray];
          }
          
          console.log('DocumentView: Final unique metadata array:', metadataArray);
          setMetadata(metadataArray);
          
        } catch (metadataError) {
          console.error("Error fetching metadata:", metadataError);
          setMetadata([]);
        }
        
      } catch (error) {
        console.error("Error fetching document:", error);
        setDoc(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDocument();
    }
  }, [id]);



  // ✅ ADD: Load associated courses when document and user are available
  useEffect(() => {
    if (doc?.id && user) {
      console.log('DocumentView: Document and user loaded, fetching associated courses');
      loadAssociatedCourses();
    }
  }, [doc?.id, user, loadAssociatedCourses]);

  // Handle conversation activation from AIChatCard
  const handleConversationActivated = useCallback((conversation) => {
    console.log('[DocumentView] Conversation activated:', conversation);
    // Update the conversation state with the activated conversation
    setDocumentConversation(conversation);
    conversationLoadedRef.current = true;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('DocumentView: Component unmounting');
      conversationLoadedRef.current = false;
    };
  }, []);



  const handleEditMetadata = () => {
    if (isEditingMetadata) {
      // Save changes
      console.log('Saving metadata changes:', editedMetadata);
      // TODO: Implement save API call here
      setIsEditingMetadata(false);
    } else {
      // Enter edit mode
      const currentMetadata = {};
      sortedMetadata.forEach(meta => {
        const fieldName = meta.metadata_definition?.name || meta.metadataName || `Field #${meta.id}`;
        currentMetadata[fieldName] = meta.value || '';
      });
      setEditedMetadata(currentMetadata);
      setIsEditingMetadata(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingMetadata(false);
    setEditedMetadata({});
  };

  const handleMetadataChange = (fieldName, value) => {
    setEditedMetadata(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const toggleChatExpansion = () => {
    setIsChatFullExpanded(!isChatFullExpanded);
  };
  
  // Edit mode state for metadata
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [editedMetadata, setEditedMetadata] = useState({});
  
  // Chat expansion state for 50/50 split
  const [isChatFullExpanded, setIsChatFullExpanded] = useState(false);

  // Chat height sizing for sidebar layout
  useEffect(() => {
    let frame = null;
    let lastApplied = null;
    const sizeChat = () => {
      const el = chatPanelWrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.top;
      const viewportH = window.innerHeight;
      const container = document.querySelector('.document-view-container');
      let padBottom = 0;
      if (container) {
        const cs = getComputedStyle(container);
        padBottom = parseInt(cs.paddingBottom || '0', 10);
      }
      const breathing = 20; // More breathing room for sidebar
      const next = Math.max(400, viewportH - top - Math.max(breathing, padBottom * 0.4));
      if (lastApplied !== next) {
        el.style.height = next + 'px';
        lastApplied = next;
      }
    };
    const schedule = () => { if (frame) cancelAnimationFrame(frame); frame = requestAnimationFrame(sizeChat); };
    schedule();
    const onResize = () => schedule();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);



  if (loading) {
    return (
      <div className="document-view-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="document-view-container">
        <div className="empty-state">
          <h3>Document Not Found</h3>
          <p>The requested document could not be found.</p>
          <button className="ui-btn ui-btn--primary" onClick={() => navigate("/documents")}>Return to Documents</button>
        </div>
      </div>
    );
  }

  const sortedMetadata = sortMetadata(metadata);

  return (
    <div className={"document-view-container document-view-grid animate-fade-in-up" + (embedded ? " is-embedded" : "")}>
      <div className={`document-view-layout ${isChatFullExpanded ? 'chat-expanded' : ''} ${showRightSidebar ? '' : 'sidebar-hidden'}`}>
        {/* Left Column: Document Preview */}
        <main className="document-main">
          {doc?.id && doc?.name ? (
            <DocumentPreview 
              key={`doc-${doc.id}`}
              documentId={doc.id} 
              documentName={doc.name}
              className="document-main-preview"
            />
          ) : (
            <div className="document-preview-container document-main-preview">
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading document preview...</p>
              </div>
            </div>
          )}
        </main>

        {/* Right Column: Tabbed Content (Chat, Metadata, Notes) */}
        {showRightSidebar && (
        <aside className="right-sidebar">
          <Tabs defaultIndex={focusTabIndex || 0} key={`tabs-${focusTabIndex}`}>
            <Tabs.List>
              {onSwitchToBrowse && (
                <button
                  type="button"
                  className="docview-browse-toggle"
                  onClick={onSwitchToBrowse}
                  title="Show documents list"
                  aria-label="Show documents list"
                >
                  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                    <rect x="1" y="1" width="16" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    <rect x="1" y="1" width="6.5" height="16" rx="2.5" fill="currentColor" opacity="0.5" />
                    <line x1="7.5" y1="1" x2="7.5" y2="17" stroke="currentColor" strokeWidth="1" opacity="0.35" />
                  </svg>
                </button>
              )}
              <Tabs.Tab><span className="docview-tab-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></span><span className="docview-tab-label">Notes</span></Tabs.Tab>
              <Tabs.Tab><span className="docview-tab-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span><span className="docview-tab-label">Chat</span></Tabs.Tab>
              <Tabs.Tab><span className="docview-tab-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></span><span className="docview-tab-label">Tools</span></Tabs.Tab>
              <Tabs.Tab><span className="docview-tab-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg></span><span className="docview-tab-label">Metadata</span></Tabs.Tab>
            </Tabs.List>

            <Tabs.Panels>
              {/* Tab 1: Document Notes */}
              <Tabs.Panel>
                <div className="notes-tab-content">
                  <DocumentNotes 
                    documentId={doc?.id} 
                    documentName={doc?.name}
                  />
                </div>
              </Tabs.Panel>

              {/* Tab 2: Document Chat */}
              <Tabs.Panel>
                <div className="chat-sidebar scoutdesk-full">
                  <div className="chat-sidebar-content">
                    <div ref={chatPanelWrapperRef} className="document-chat-viewport">
                      <div className="document-chat-inner">
                        <AIChatCard 
                          mode="document" 
                          documentId={doc?.id} 
                          documentName={doc?.name}
                          conversation={documentConversation}
                          isExpanded={isChatFullExpanded}
                          onToggleExpansion={toggleChatExpansion}
                          onConversationActivated={handleConversationActivated}
                          hideHeader
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Tabs.Panel>

              {/* Tab 3: Study Tools */}
              <Tabs.Panel>
                <StudyToolsTab documentId={doc?.id} documentName={doc?.name} />
              </Tabs.Panel>

              {/* Tab 4: Document Metadata */}
              <Tabs.Panel>
                <div className="metadata-sidebar">
                  <div className="metadata-sidebar-content">
                    <div className="sidebar-header">
                      <h2 className="sidebar-title">Document Metadata</h2>
                      <button
                        className={`edit-document-icon ${isEditingMetadata ? 'save-mode' : ''}`}
                        onClick={handleEditMetadata}
                        title={isEditingMetadata ? "Save Changes" : "Edit Metadata"}
                      >
                        {isEditingMetadata ? "Save ✅" : "Edit ✏️"}
                      </button>
                      {isEditingMetadata && (
                        <button
                          className="cancel-edit-icon"
                          onClick={handleCancelEdit}
                          title="Cancel Edit"
                        >
                          Cancel ✕
                        </button>
                      )}
                    </div>
                    <div className="metadata-scroll-container">
                      {sortedMetadata.length === 0 && associatedCourses.length === 0 ? (
                        <div className="empty-state">
                          <h3>No Metadata Found</h3>
                          <p>This document doesn't have any metadata yet.</p>
                        </div>
                      ) : (
                        <table className="metadata-table">
                          <tbody>
                            <tr key="associated-courses-display">
                              <td className="meta-label">Associated Courses</td>
                              <td className="meta-value">
                                {coursesLoading ? (
                                  <div className="loading-state-inline">
                                    <div className="loading-spinner small"></div>
                                    <span>Loading courses...</span>
                                  </div>
                                ) : associatedCourses.length > 0 ? (
                                  <div className="tag-container">
                                    {associatedCourses.map(course => (
                                      <span key={course.endeavorId || course.id} className="tag-pill">
                                        {course.name || course.title || `Course ${course.endeavorId || course.id}`}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="meta-empty">No courses associated</span>
                                )}
                              </td>
                            </tr>
                            {sortedMetadata.map((meta, index) => {
                              const fieldName = meta.metadata_definition?.name || meta.metadataName || `Field #${meta.id}`;
                              const displayValue = isEditingMetadata ? editedMetadata[fieldName] : meta.value;
                              return (
                                <tr key={`metadata-${meta.id}-${index}`}>
                                  <td className="meta-label">{fieldName}</td>
                                  <td className="meta-value">
                                    {renderFieldValue(fieldName, displayValue, isEditingMetadata, handleMetadataChange)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Panel>
            </Tabs.Panels>
          </Tabs>
        </aside>
        )}
      </div>
    </div>
  );
}

export default DocumentView;
